<script setup lang="ts">
import { RouterLink } from 'vue-router'
import { ActionItem, ActionsGroup, ActionsMenu, VProgressBar, VTable } from '@/modules/shared/components'
import { safeFormat } from '@/modules/shared/utils/date'
import type { Message } from '../types'

defineProps<{
  messages: Message[]
  skeleton: boolean
  isAdmin: boolean
}>()

const emit = defineEmits<{
  delete: [id: number]
  view: [id: number]
}>()

const columns = [
  { key: 'subject', name: 'Subject', type: 'string', align: 'left', is_visible: true, fixed: true },
  { key: 'sender_name', name: 'Sender', type: 'string', align: 'left', is_visible: true },
  { key: 'to_in_words', name: 'Recipients', type: 'string', align: 'left', is_visible: true },
  { key: 'percentage_opened', name: '% Opened', type: 'number', align: 'right', is_visible: true },
  { key: 'created_at', name: 'Date', type: 'date', align: 'left', is_visible: true, sorted: true, sort_order: 'desc' },
  { key: 'actions', name: '', type: 'actions', align: 'right', is_visible: true },
]

const onDelete = (id: number) => {
  if (!window.confirm('Are you sure you want to delete this message?')) return
  emit('delete', id)
}
</script>

<template>
  <VTable
    name="messaging-messages"
    :columns="columns"
    :items="messages"
    :skeleton="skeleton"
    :slots="['subject', 'to_in_words', 'percentage_opened', 'created_at', 'actions']"
    no_data_message="No messages found."
  >
    <template #subject="{ item }">
      <RouterLink class="group" :to="{ name: 'messaging.show', params: { id: item.id } }">
        <span class="font-medium text-gray-900 group-hover:text-[#3b88af]">{{ item.subject }}</span>
      </RouterLink>
    </template>

    <template #to_in_words="{ item }">
      <span class="block max-w-[12rem] truncate" :title="item.to_in_words">
        {{ item.to_in_words }}
      </span>
    </template>

    <template #percentage_opened="{ item }">
      <div class="flex items-center gap-2">
        <VProgressBar :progress="item.percentage_opened / 100" color="v-blue" class="w-16" />
      </div>
    </template>

    <template #created_at="{ item }">
      {{ safeFormat(item.created_at, 'MMM d, yyyy') }}
    </template>

    <template #actions="{ item }">
      <div class="flex items-center justify-end">
        <ActionsMenu v-if="isAdmin">
          <ActionsGroup>
            <ActionItem icon="Trash" text="Delete" @click="onDelete(item.id)" />
          </ActionsGroup>
        </ActionsMenu>
      </div>
    </template>
  </VTable>
</template>
